import { useState } from 'react';
import { getApiErrorMessage } from '../services/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  Button,
  Spinner,
} from '@/components/ui';

interface ConfirmationModalProps {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  children?: React.ReactNode;
  isDanger?: boolean;
  open?: boolean;
}

export default function ConfirmationModal({
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  children,
  isDanger = false,
  open = true
}: ConfirmationModalProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setIsProcessing(true);
    setError(null);
    
    try {
      await onConfirm();
      // Modal will be closed by parent component
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Operation failed'));
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className={isDanger ? 'text-destructive' : ''}>
            {isDanger && '⚠️ '}{title}
          </DialogTitle>
        </DialogHeader>
        
        {isDanger && (
          <div className="p-4 bg-destructive-light border-l-4 border-destructive rounded">
            <p className="m-0 text-destructive font-medium">
              This action cannot be undone!
            </p>
          </div>
        )}

        <div className="py-2">
          <DialogDescription className="mb-3">{message}</DialogDescription>
          {children}
        </div>

        {error && (
          <div className="p-3 bg-destructive-light border border-destructive rounded text-destructive text-sm">
            {error}
          </div>
        )}

        <DialogFooter>
          <Button 
            variant="secondary" 
            onClick={onCancel}
            disabled={isProcessing}
          >
            {cancelText}
          </Button>
          <Button 
            variant={isDanger ? 'destructive' : 'default'}
            onClick={handleConfirm}
            disabled={isProcessing}
          >
            {isProcessing && <Spinner size="sm" className="mr-2" />}
            {isProcessing ? 'Processing...' : confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
